<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import { emitter } from 'shared/helpers/mitt';

// [factorem-grace-side-panel] Dashboard apps with these titles render as a
// side panel next to the messages instead of as a tab. The dashboard_apps API
// only permits content[].url/type, so the title is the only stable flag.
const SIDE_PANEL_APP_TITLES = ['grace', 'grace ai'];
const SIDE_PANEL_DEFAULT_WIDTH = 420;
const SIDE_PANEL_MIN_WIDTH = 300;
const SIDE_PANEL_MAX_WIDTH = 900;
const SIDE_PANEL_STORAGE_KEY = 'factorem.gracePanel';
// [factorem-grace-message-select] Clicking a message bubble tells the side
// panel apps which message was clicked. Message.vue renders each bubble with
// data-message-id; clicks on interactive elements or that end a text
// selection are ignored so links, attachments and menus keep working.
const MESSAGE_SELECTED_EVENT = 'factorem:messageSelected';
const MESSAGE_CLICK_IGNORE_SELECTOR =
  'a, button, input, textarea, select, label, img, video, audio, [role="button"], [contenteditable="true"]';

// [factorem-grace-reading] The side panel tells us which message it is
// reading (factorem:readingMessage) or asks us to jump to one
// (factorem:scrollToMessage); that message row gets a purple edge + "G" dot.
// It can also ask to open the Ask Grace page (factorem:openAsk). Only
// messages whose source is a side-panel iframe are accepted.
const READING_MESSAGE_EVENT = 'factorem:readingMessage';
const SCROLL_TO_MESSAGE_EVENT = 'factorem:scrollToMessage';
const OPEN_ASK_EVENT = 'factorem:openAsk';
// [factorem-grace-insert-reply] factorem:insertReply {text} is re-emitted on
// the bus; ReplyBox.vue puts it in the Reply composer (never sends).
const INSERT_REPLY_EVENT = 'factorem:insertReply';
const INSERT_REPLY_BUS_EVENT = 'factorem-grace-insert-reply';
const READING_STYLE_ID = 'factorem-grace-reading-style';

const parseFrameMessage = raw => {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string' || !raw.startsWith('{')) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Highlight by attribute selector in a <style> tag, so it survives Vue
// re-rendering the row (class patching) or re-creating it after a load.
const readingHighlightCss = messageId => {
  const row = `.factorem-grace-message-select [data-message-id="${messageId}"]`;
  return `
${row} { position: relative; }
${row}::after { content: ''; position: absolute; top: 2px; bottom: 2px; inset-inline-start: -9px; width: 3px; border-radius: 3px; background: rgb(var(--violet-9)); pointer-events: none; }
.dark ${row}::after { background: rgb(var(--violet-11)); }
${row}::before { content: 'G'; position: absolute; top: -5px; inset-inline-start: -15.5px; z-index: 1; width: 16px; height: 16px; border-radius: 9999px; background: rgb(var(--violet-9)); color: #fff; font: 600 10px/16px system-ui, sans-serif; text-align: center; pointer-events: none; user-select: none; box-shadow: 0 1px 2px rgb(0 0 0 / 0.25); }
`;
};

const readSidePanelPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(SIDE_PANEL_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const prefs = readSidePanelPrefs();
    return {
      activeIndex: 0,
      sidePanelCollapsed: !!prefs.collapsed,
      sidePanelWidth: prefs.width || SIDE_PANEL_DEFAULT_WIDTH,
      sidePanelResizing: false,
      graceReadingMessageId: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    sideDashboardApps() {
      return this.dashboardApps.filter(app =>
        SIDE_PANEL_APP_TITLES.includes((app.title || '').trim().toLowerCase())
      );
    },
    tabDashboardApps() {
      return this.dashboardApps.filter(
        app => !this.sideDashboardApps.includes(app)
      );
    },
    // Grace's origin: messages are only sent to it and only accepted from it
    sidePanelOrigin() {
      try {
        return new URL(this.sideDashboardApps[0]?.content?.[0]?.url).origin;
      } catch {
        return '';
      }
    },
    showSidePanel() {
      return !!(this.currentChat.id && this.sideDashboardApps.length);
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.tabDashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
    graceHighlightedMessageId() {
      if (!this.showSidePanel || this.sidePanelCollapsed) return null;
      return this.graceReadingMessageId;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler() {
        if (this.currentChat.inbox_id && this.currentChat.id) {
          this.$store.dispatch('inboxAssignableAgents/fetch', {
            inboxIds: [this.currentChat.inbox_id],
            includeAIAssignees: true,
          });
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
      this.graceReadingMessageId = null;
    },
    graceHighlightedMessageId(messageId) {
      this.renderReadingHighlight(messageId);
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    window.addEventListener('message', this.onSidePanelMessage);
  },
  unmounted() {
    this.stopSidePanelResize();
    window.removeEventListener('message', this.onSidePanelMessage);
    this.renderReadingHighlight(null);
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onMessageAreaClick(event) {
      if (!this.showSidePanel || event.button !== 0) return;
      const target = event.target instanceof Element ? event.target : null;
      const bubble = target?.closest('[data-message-id]');
      if (!bubble || target.closest(MESSAGE_CLICK_IGNORE_SELECTOR)) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && selection.toString().trim())
        return;
      const messageId = Number(bubble.dataset.messageId);
      if (!messageId) return;
      const payload = JSON.stringify({
        event: MESSAGE_SELECTED_EVENT,
        data: { messageId },
      });
      if (!this.sidePanelOrigin) return;
      this.$el
        .querySelectorAll('.factorem-grace-side-panel iframe')
        .forEach(frame =>
          frame.contentWindow?.postMessage(payload, this.sidePanelOrigin)
        );
    },
    isFromSidePanel(source) {
      if (!source || !this.$el?.querySelectorAll) return false;
      return [
        ...this.$el.querySelectorAll('.factorem-grace-side-panel iframe'),
      ].some(frame => frame.contentWindow === source);
    },
    onSidePanelMessage(event) {
      if (
        !this.showSidePanel ||
        event.origin !== this.sidePanelOrigin ||
        !this.isFromSidePanel(event.source)
      )
        return;
      const message = parseFrameMessage(event.data);
      if (!message || typeof message.event !== 'string') return;
      const data = message.data || {};
      if (message.event === OPEN_ASK_EVENT) {
        const q = typeof data.q === 'string' ? data.q.trim() : '';
        this.$router.push({
          name: 'factorem_grace_ask',
          params: { accountId: this.$route.params.accountId },
          query: q ? { q } : {},
        });
        return;
      }
      if (message.event === INSERT_REPLY_EVENT) {
        if (typeof data.text === 'string' && data.text.trim()) {
          emitter.emit(INSERT_REPLY_BUS_EVENT, data.text);
        }
        return;
      }
      const messageId = Number(data.messageId);
      if (!Number.isInteger(messageId) || messageId <= 0) return;
      if (message.event === READING_MESSAGE_EVENT) {
        this.graceReadingMessageId = messageId;
      } else if (message.event === SCROLL_TO_MESSAGE_EVENT) {
        this.graceReadingMessageId = messageId;
        this.scrollToGraceMessage(messageId);
      }
    },
    findMessageRow(messageId) {
      return this.$el.querySelector(
        `.factorem-grace-message-select [data-message-id="${messageId}"]`
      );
    },
    async scrollToGraceMessage(messageId) {
      const conversationId = this.currentChat.id;
      let row = this.findMessageRow(messageId);
      if (!row) {
        // Same load-around mechanism as ConversationView#setActiveChat
        // (?messageId=): fetch from the target up to the oldest loaded
        // message. Only when the target is older than what is loaded;
        // anything else is not in this conversation, so ignore it.
        const oldestLoadedId = this.currentChat.messages?.[0]?.id;
        if (!oldestLoadedId || messageId >= oldestLoadedId) return;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId,
            after: messageId,
            before: oldestLoadedId,
          });
        } catch {
          return;
        }
        await this.$nextTick();
        // let MessagesView finish its own post-load scroll adjustment first
        await new Promise(resolve => {
          requestAnimationFrame(resolve);
        });
        if (this.currentChat.id !== conversationId) return;
        row = this.findMessageRow(messageId);
        if (!row) return;
      }
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    renderReadingHighlight(messageId) {
      let style = document.getElementById(READING_STYLE_ID);
      if (!messageId) {
        style?.remove();
        return;
      }
      if (!style) {
        style = document.createElement('style');
        style.id = READING_STYLE_ID;
        document.head.appendChild(style);
      }
      style.textContent = readingHighlightCss(Number(messageId));
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
    saveSidePanelPrefs() {
      try {
        localStorage.setItem(
          SIDE_PANEL_STORAGE_KEY,
          JSON.stringify({
            collapsed: this.sidePanelCollapsed,
            width: this.sidePanelWidth,
          })
        );
      } catch {
        // storage unavailable: keep in-memory state only
      }
    },
    toggleSidePanel() {
      this.sidePanelCollapsed = !this.sidePanelCollapsed;
      this.saveSidePanelPrefs();
    },
    startSidePanelResize(event) {
      event.preventDefault();
      this.sidePanelResizing = true;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = this.sidePanelWidth;
      window.addEventListener('pointermove', this.onSidePanelResize);
      window.addEventListener('pointerup', this.stopSidePanelResize);
    },
    onSidePanelResize(event) {
      const isRtl = document.documentElement.dir === 'rtl';
      const delta = (this.resizeStartX - event.clientX) * (isRtl ? -1 : 1);
      this.sidePanelWidth = Math.min(
        SIDE_PANEL_MAX_WIDTH,
        Math.max(SIDE_PANEL_MIN_WIDTH, this.resizeStartWidth + delta)
      );
    },
    stopSidePanelResize() {
      window.removeEventListener('pointermove', this.onSidePanelResize);
      window.removeEventListener('pointerup', this.stopSidePanelResize);
      if (this.sidePanelResizing) {
        this.sidePanelResizing = false;
        this.saveSidePanelPrefs();
      }
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !tabDashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="tabDashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <div
        class="factorem-grace-message-select relative flex flex-1 min-w-0 h-full min-h-0 m-0"
        @click="onMessageAreaClick"
      >
        <MessagesView
          v-if="currentChat.id"
          :inbox-id="inboxId"
          :is-inbox-view="isInboxView"
        />
        <EmptyState
          v-if="!currentChat.id && !isInboxView"
          :is-on-expanded-layout="isOnExpandedLayout"
        />
        <slot />
      </div>
      <!-- [factorem-grace-side-panel] -->
      <aside
        v-if="showSidePanel"
        class="factorem-grace-side-panel relative hidden md:flex flex-col flex-shrink-0 h-full min-h-0 border-l rtl:border-l-0 rtl:border-r border-n-weak bg-n-surface-1"
        :style="sidePanelCollapsed ? {} : { width: `${sidePanelWidth}px` }"
      >
        <div
          v-if="!sidePanelCollapsed"
          class="absolute inset-y-0 ltr:-left-1 rtl:-right-1 w-2 cursor-col-resize z-10 hover:bg-n-weak/60"
          :class="{ 'bg-n-weak/60': sidePanelResizing }"
          @pointerdown="startSidePanelResize"
        />
        <div
          class="flex items-center gap-1 h-9 px-2 border-b border-n-weak text-sm text-n-slate-11 flex-shrink-0"
          :class="{ 'flex-col !h-auto py-2 px-1 border-b-0': sidePanelCollapsed }"
        >
          <button
            type="button"
            class="flex items-center justify-center size-7 rounded-md hover:bg-n-alpha-2 text-n-slate-11"
            :title="sidePanelCollapsed ? 'Show panel' : 'Hide panel'"
            @click="toggleSidePanel"
          >
            <span
              class="size-4"
              :class="
                sidePanelCollapsed
                  ? 'i-lucide-panel-right-open'
                  : 'i-lucide-panel-right-close'
              "
            />
          </button>
          <span
            v-if="!sidePanelCollapsed"
            class="font-medium text-n-slate-12 truncate"
          >
            {{ sideDashboardApps.map(app => app.title).join(' · ') }}
          </span>
          <span
            v-else
            class="font-medium text-n-slate-12 [writing-mode:vertical-rl]"
          >
            {{ sideDashboardApps[0].title }}
          </span>
        </div>
        <div
          v-show="!sidePanelCollapsed"
          class="flex flex-col flex-1 min-h-0"
          :class="{ 'pointer-events-none': sidePanelResizing }"
        >
          <DashboardAppFrame
            v-for="(dashboardApp, index) in sideDashboardApps"
            :key="currentChat.id + '-side-' + dashboardApp.id"
            class="flex-1 min-h-0"
            is-visible
            :config="dashboardApp.content"
            :position="1000 + index"
            :current-chat="currentChat"
          />
        </div>
      </aside>
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in tabDashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApp.content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
